<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'

type NoticeTone = 'info' | 'warning' | 'success'

const props = withDefaults(
  defineProps<{
    message?: string | null
    tone?: NoticeTone
    dismissible?: boolean
    dismissLabel?: string
    noticeKey?: string | number
  }>(),
  {
    message: null,
    tone: 'info',
    dismissible: false,
    dismissLabel: 'Dismiss notice',
    noticeKey: undefined,
  },
)

const emit = defineEmits<{
  dismiss: []
}>()

const slots = useSlots()
const dismissed = ref(false)
const slotMode = computed(() => Boolean(slots.default))
const hasContent = computed(() => slotMode.value || Boolean(props.message?.trim()))

watch(
  () => (slotMode.value ? props.noticeKey : props.message),
  () => {
    dismissed.value = false
  },
)

function dismiss(): void {
  emit('dismiss')
  dismissed.value = true
}

const iconClass = computed(() => {
  if (props.tone === 'warning') return 'pi-exclamation-triangle'
  if (props.tone === 'success') return 'pi-check-circle'
  return 'pi-info-circle'
})
</script>

<template>
  <div
    v-if="hasContent && !dismissed"
    class="notice-banner"
    :class="`notice-banner--${tone}`"
    :data-tone="tone"
    data-testid="app-notice-banner"
  >
    <div class="notice-banner__status" role="status" aria-live="polite" aria-atomic="true">
      <i class="pi notice-banner__icon" :class="iconClass" aria-hidden="true" />
      <div class="notice-banner__content">
        <slot v-if="slotMode" />
        <p v-else class="notice-banner__message">{{ message }}</p>
      </div>
    </div>
    <div v-if="$slots.actions" class="notice-banner__actions">
      <slot name="actions" />
    </div>
    <button
      v-if="dismissible"
      type="button"
      class="notice-banner__dismiss"
      :aria-label="dismissLabel"
      @click="dismiss"
    >
      <i class="pi pi-times" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.notice-banner {
  --notice-surface: var(--color-info-surface);
  --notice-text: var(--color-info-text);
  --notice-border: var(--color-info-border);
  --notice-accent: var(--color-info-accent);

  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  margin-bottom: var(--space-4);
  color: var(--notice-text);
  background: var(--notice-surface);
  border: var(--space-px) solid var(--notice-border);
  border-left: var(--space-1) solid var(--notice-accent);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}

.notice-banner--warning {
  --notice-surface: var(--color-warning-surface);
  --notice-text: var(--color-warning-text);
  --notice-border: var(--color-warning-border);
  --notice-accent: var(--color-warning-accent);
}

.notice-banner--success {
  --notice-surface: var(--color-success-surface);
  --notice-text: var(--color-success-text);
  --notice-border: var(--color-success-border);
  --notice-accent: var(--color-success-accent);
}

.notice-banner__status {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  flex: 1;
  min-width: 0;
}

.notice-banner__icon {
  flex-shrink: 0;
  margin-top: var(--space-px);
  font-size: var(--font-size-xl);
  color: var(--notice-accent);
}

.notice-banner__content {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-md);
  font-weight: 500;
  line-height: 1.5;
}

.notice-banner__message {
  margin: 0;
}

.notice-banner__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.notice-banner__dismiss {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--space-12);
  height: var(--space-12);
  padding: 0;
  color: var(--notice-accent);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  opacity: 0.6;
  transition: opacity var(--duration-ui) var(--ease-default);
}

.notice-banner__dismiss:hover {
  opacity: 1;
}

.notice-banner__dismiss:focus-visible {
  outline: var(--focus-ring-width) solid var(--notice-accent);
  outline-offset: var(--space-px);
}

.notice-banner__dismiss .pi {
  font-size: var(--font-size-xs);
}
</style>
